import { useEffect, useRef, useState } from 'react';
import type { SipMediaState } from './sip-softphone';
import type { LocalSipRegistrationState, ProviderRegistrationState } from './types';

export const browserSessionAuthLostEvent = 'sparkone:auth-lost';


export type BrowserRuntimeLifecycle =
  | 'idle'
  | 'electing'
  | 'preparing'
  | 'requesting_permission'
  | 'registering'
  | 'ready'
  | 'incoming_unenriched'
  | 'ringing'
  | 'answering'
  | 'connected'
  | 'ending'
  | 'degraded'
  | 'unavailable'
  | 'failed';

export type MirroredBrowserDialog = {
  dialogId: string;
  state: 'ringing' | 'answering' | 'connected';
  sipCorrelationId: string | null;
  /** True only after Spark VP authorizes this exact inbound device leg. */
  answerAuthorized: boolean;
  /** Opaque canonical binding used only to safely enrich this local dialog. */
  authorizedCallSessionId?: string;
  authorizedParticipantId?: string;
  authorizedEndpointDeviceId?: string;
};

export type BrowserRuntimeNotice = {
  kind:
    | 'answered_elsewhere'
    | 'rejected'
    | 'call_failed'
    | 'media_failure'
    | 'registration_degraded'
    | 'recovery_degraded'
    | 'correlation_timeout'
    | 'correlation_conflict';
  message: string;
  observedAt: number;
};

export type BrowserRuntimePublicState = {
  lifecycle: BrowserRuntimeLifecycle;
  dialogs: MirroredBrowserDialog[];
  /** Direction-independent answering/connected SIP-dialog authority fence. */
  hasActiveCall: boolean;
  isMuted: boolean;
  mediaState: SipMediaState;
  localRegistrationState: LocalSipRegistrationState;
  providerRegistrationState: ProviderRegistrationState;
  message: string | null;
  notice?: BrowserRuntimeNotice | null;
};

// ===========================================================================
// Multi-tab leader election (browser-phone arbitration)
// ---------------------------------------------------------------------------
// SAFETY: this is the coordination layer that prevents two tabs of the same
// user from racing over one Browser SIP registration. It MUST NEVER block
// calling entirely. If `navigator.locks` is unavailable (SSR, privacy mode,
// old browser, jsdom), or if arbitration is disabled with the flag below, the
// hook returns `{ owned: true, dormant: false }` so behavior degrades to the
// pre-arbitration single-tab model. If this coordination misbehaves in
// testing, flip `BROWSER_PHONE_LEADERSHIP_ENABLED` to `false` to disable it
// globally and fall back to always-active. The server-side
// VOICE_BROWSER_ACTIVATION_COLLISION 409 remains the hard backstop for any
// residual cross-tab race regardless of this flag.
// ===========================================================================

/**
 * Global kill-switch for browser-phone leader election. Flip to `false` to
 * disable multi-tab arbitration and restore always-active behavior.
 */
export const BROWSER_PHONE_LEADERSHIP_ENABLED = true;

export const browserPhoneLeadershipChannelName = 'sparkone.voice.browser-phone';
const browserPhoneLeadershipLockPrefix = 'sparkone.voice.browser-phone.';

/** Builds the per-user exclusive Web Lock name used for leader election. */
export function browserPhoneLeadershipLockName(userId: string): string {
  return `${browserPhoneLeadershipLockPrefix}${userId}`;
}

export type BrowserPhoneLeadershipState = {
  /** True when this tab may ensure the installation and start the SIP UA. */
  owned: boolean;
  /** True when another tab is known to lead and this tab must stay idle. */
  dormant: boolean;
  /** Increments each time this tab acquires its scoped Web Lock. */
  epoch: number;
};

type LeadershipMessage =
  | { kind: 'active'; lockName: string }
  | { kind: 'who_is_active'; lockName: string };

type NavigatorLocks = {
  request: (
    name: string,
    options: { mode: 'exclusive' },
    callback: (lock: { name: string } | null) => Promise<void> | void,
  ) => Promise<void>;
};

type BroadcastChannelLike = {
  postMessage: (message: LeadershipMessage) => void;
  close: () => void;
  onmessage: ((event: { data: LeadershipMessage }) => void) | null;
};

function readNavigatorLocks(): NavigatorLocks | null {
  if (typeof navigator === 'undefined') return null;
  const locks = (navigator as { locks?: NavigatorLocks }).locks;
  if (!locks || typeof locks.request !== 'function') return null;
  return locks;
}

function createLeadershipChannel(): BroadcastChannelLike | null {
  if (typeof globalThis === 'undefined') return null;
  const Ctor = (
    globalThis as unknown as {
      BroadcastChannel?: new (name: string) => BroadcastChannelLike;
    }
  ).BroadcastChannel;
  if (typeof Ctor !== 'function') return null;
  try {
    return new Ctor(browserPhoneLeadershipChannelName);
  } catch {
    return null;
  }
}

/**
 * Exclusive multi-tab leader election for the browser phone. The holder of the
 * per-user Web Lock is the single "active" tab that may ensure the Browser
 * installation and start the SIP UA; all other tabs are dormant and render a
 * "calling active in another tab" state. The lock auto-releases when the active
 * tab closes or navigates, so a queued dormant tab takes over immediately.
 *
 * A BroadcastChannel accelerates local arbitration: the active tab announces
 * itself, and queued tabs ask who is active so they learn dormancy without a
 * server round-trip. The election is OPTIMISTIC on the active tab (it starts
 * `owned: true`) so the common single-tab case never flashes dormant; a queued
 * tab learns dormancy from the holder's broadcast and from the server's
 * VOICE_BROWSER_ACTIVATION_COLLISION 409, which remains the hard backstop.
 */
export function useBrowserPhoneLeadership(
  userId: string | null | undefined,
): BrowserPhoneLeadershipState {
  // SAFETY: optimistic start. Without Web Locks, without a user scope, or when
  // arbitration is disabled, this tab always behaves as the single active tab.
  const [leadership, setLeadership] = useState({ owned: true, epoch: 0 });
  const heldRef = useRef(false);

  useEffect(() => {
    heldRef.current = false;
    if (!BROWSER_PHONE_LEADERSHIP_ENABLED || !userId) return;
    const locks = readNavigatorLocks();
    if (!locks) return; // SAFETY: stay optimistic + active.

    let cancelled = false;
    let release: (() => void) | null = null;
    const lockName = browserPhoneLeadershipLockName(userId);
    const channel = createLeadershipChannel();

    const announceActive = (): void => {
      try {
        channel?.postMessage({ kind: 'active', lockName });
      } catch {
        /* broadcast failure is harmless; the lock is the source of truth */
      }
    };

    if (channel) {
      channel.onmessage = (event: { data: LeadershipMessage }): void => {
        if (cancelled) return;
        const message = event?.data;
        if (!message || message.lockName !== lockName) return;
        if (message.kind === 'active') {
          // Another tab holds the lock. If we do not hold it, we are dormant.
          // Our own lock callback is the ultimate authority and will flip us
          // back to active when we acquire the lock.
          if (!heldRef.current) {
            setLeadership((current) => ({ ...current, owned: false }));
          }
          return;
        }
        if (message.kind === 'who_is_active' && heldRef.current) {
          announceActive();
        }
      };
    }

    // Ask any existing holder to announce itself so this tab learns dormancy
    // without waiting for its own (queued) lock callback to fire.
    try {
      channel?.postMessage({ kind: 'who_is_active', lockName });
    } catch {
      /* ignore */
    }

    void locks
      .request(
        lockName,
        { mode: 'exclusive' },
        async (lock) => {
          if (!lock || cancelled) return;
          heldRef.current = true;
          // Increment even when this tab was optimistic-owned already. Runtime
          // bootstrap uses this acquisition epoch to recover from a prior 409
          // collision once this queued tab finally receives the lock.
          setLeadership((current) => ({
            owned: true,
            epoch: current.epoch + 1,
          }));
          announceActive();
          // Hold the lock until teardown. The browser auto-releases on tab
          // close/navigation, letting a queued dormant tab take over.
          await new Promise<void>((resolve) => {
            release = resolve;
          });
          heldRef.current = false;
        },
      )
      .catch(() => {
        /* acquire failure is harmless; server 409 remains the backstop */
      });

    return () => {
      cancelled = true;
      release?.();
      heldRef.current = false;
      try {
        channel?.close();
      } catch {
        /* ignore */
      }
    };
  }, [userId]);

  return {
    owned: leadership.owned,
    dormant: !leadership.owned,
    epoch: leadership.epoch,
  };
}
